import { api } from './api'

export interface EaStatus {
  connected: boolean
  spread_points: number | null
  equity: number | null
  balance: number | null
  open_positions: number | null
  bid: number | null
  ask: number | null
  last_seen: string | null
  ea_token: string | null
  trade_allowed: boolean | null
  not_ready_reason: string | null
}

export interface IxenPair {
  id: number
  display_name: string
  prop_login: string
  prop_server: string
  prop_size: number
  prop_provider: string
  prop_provider_label: string
  prop_phase: string
  prop_phase_label: string
  broker_login: string
  broker_server: string
  ea_prop: EaStatus
  ea_broker: EaStatus
  is_active: boolean
  is_locked: boolean
  created_at: string
  // ── Cap dinamici autoritativi (mirror backend _compute_dynamic_caps) ────
  current_equity: number | null
  payout_target_equity: number | null
  payout_target_reached: boolean
  dd_floor_equity: number | null
  sl_max_dynamic: number
  tp_max_dynamic: number
  sl_max_static: number
  tp_max_static: number
  buffer_dollar: number | null
  remaining_to_target: number | null
  is_real_phase: boolean
  // Modalita' Zanshin per questa pair (default AUTO). Se MANUAL, le decisioni
  // del motore richiedono approvazione esplicita entro 60s (vedi ZanshinApprovalsPanel).
  zanshin_mode?: 'AUTO' | 'MANUAL'
  // Modalita' Controllo (prop-only, fire-and-forget): invia l'ordine solo
  // all'EA PROP (MT5), nessun broker, nessun tracking/rollback automatico.
  control_mode?: boolean
  makatsu_enabled?: boolean
  // Modalita' Disciplinata (solo provider FND): margine PROP cappato al 29%
  // del valore nominale in tutte le fasi, lotti ridotti. Default false.
  fnd_disciplined_mode?: boolean
  // Profilo rischio FP2 (solo FundingPips 2 Step, scelto alla creazione, immutabile)
  fp2_risk_profile?: 'conservativo' | 'aggressivo' | null
  // FASE 1 — stato rischio card (daily-loss netto, margine broker, finestre fine giornata UTC).
  // null quando il sistema rischio è disattivato (RISK_PHASE1_ENABLED=false).
  risk_state?: IxenRiskState | null
}

export interface IxenRiskState {
  enabled: boolean
  rollover_anchor: string
  next_close_utc: string
  seconds_to_close: number
  daily_consumed_pct: number | null
  daily_room_pct: number | null
  daily_net_dollars: number | null
  daily_net_pct: number | null
  daily_limit_pct: number
  daily_locked: boolean
  broker_margin_ok: boolean
  broker_free_margin: number | null
  broker_margin_required: number | null
  auto_open_blocked_window: boolean
  force_close_window: boolean
  card_blocked: boolean
  reset_applied?: boolean
}

export interface IxenSignal {
  id: number
  ixen_pair_id: number
  pair_name: string | null
  direction: string
  status: string
  spread_points: number | null
  spread_mode: string | null
  offset_price: number | null
  centralized: boolean
  notes: string | null
  created_at: string
  executed_at: string | null
}

export interface SignalResponse {
  status: string
  signal_id?: number
  direction?: string
  spread_mode?: string
  offset_price?: number
  message: string
  spread_points?: number
  broker_spread_points?: number | null
  sl_clamped_from?: number
  sl_clamped_to?: number
  tp_clamped_from?: number
  tp_clamped_to?: number
}

export interface PairCreateRequest {
  display_name?: string
  prop_login: string
  prop_password?: string
  prop_server: string
  prop_size: number
  prop_provider: string
  prop_phase: string
  // Solo FP2: profilo rischio obbligatorio alla creazione, immutabile
  fp2_risk_profile?: 'conservativo' | 'aggressivo'
  broker_login: string
  broker_password?: string
  broker_server: string
}

const ixenSystemService = {
  async getPairs(): Promise<{ pairs: IxenPair[] }> {
    return api.get<{ pairs: IxenPair[] }>('/ixen-system/pairs')
  },

  async createPair(data: PairCreateRequest): Promise<IxenPair> {
    return api.post<IxenPair>('/ixen-system/pairs', data)
  },

  async updatePair(pairId: number, data: { display_name?: string; prop_phase?: string; prop_size?: number; makatsu_enabled?: boolean }): Promise<IxenPair> {
    return api.patch<IxenPair>(`/ixen-system/pairs/${pairId}`, data)
  },

  async deletePair(pairId: number): Promise<void> {
    return api.delete<void>(`/ixen-system/pairs/${pairId}`)
  },

  async submitSignal(params: {
    ixen_pair_id: number
    direction: string
    centralized: boolean
    confirmed?: boolean
    sl_percent?: number
    tp_percent?: number
    funder_pro_margin_mode?: string
    fnd_leverage?: number
    signal_source?: string
    offset_mode?: 'DYNAMIC' | 'STATIC'
  }): Promise<SignalResponse> {
    return api.post<SignalResponse>('/ixen-system/signal', {
      ixen_pair_id: params.ixen_pair_id,
      direction: params.direction,
      centralized: params.centralized,
      confirmed: params.confirmed ?? false,
      sl_percent: params.sl_percent,
      tp_percent: params.tp_percent,
      funder_pro_margin_mode: params.funder_pro_margin_mode ?? 'massimale',
      fnd_leverage: params.fnd_leverage ?? 15,
      signal_source: params.signal_source,
      offset_mode: params.offset_mode ?? 'DYNAMIC',
    })
  },

  async emergencyStop(): Promise<{ status: string; pairs_count: number; message: string }> {
    return api.post<{ status: string; pairs_count: number; message: string }>('/ixen-system/emergency-stop')
  },

  // ── Manual rollback opt-in (Opzione 4) ────────────────────────────────────
  async getManualRollback(): Promise<{ enabled: boolean }> {
    return api.get<{ enabled: boolean }>('/ixen-system/me/manual-rollback')
  },

  async setManualRollback(enabled: boolean): Promise<{ enabled: boolean }> {
    return api.patch<{ enabled: boolean }>('/ixen-system/me/manual-rollback', { enabled })
  },

  async dismissRecovery(signalId: number): Promise<{ status: string }> {
    return api.post<{ status: string }>(`/ixen-system/signal/${signalId}/dismiss-recovery`)
  },

  async listManualRecoveries(): Promise<{ recoveries: any[]; count: number }> {
    return api.get<{ recoveries: any[]; count: number }>('/ixen-system/me/manual-recoveries')
  },

  async testHedge(params: {
    ixen_pair_id: number
    sl_percent: number
    tp_percent: number
  }): Promise<{
    status: string
    signal_id: number
    direction: string
    gold_price: number
    lots_prop: number
    lots_broker: number
    sl_prop: number
    tp_prop: number
    message: string
  }> {
    return api.post('/ixen-system/test-hedge', params)
  },

  async closeTestHedge(signalId: number): Promise<{
    status: 'CLOSE_SENT' | 'NOOP' | string
    signal_id: number
    close_instructions: number
    message?: string
  }> {
    return api.post(`/ixen-system/test-hedge/${signalId}/close`)
  },

  async getSignals(limit = 50): Promise<{ signals: IxenSignal[] }> {
    return api.get<{ signals: IxenSignal[] }>(`/ixen-system/signals?limit=${limit}`)
  },

  async getDashboard(signalsLimit = 30): Promise<{ pairs: IxenPair[]; signals: IxenSignal[] }> {
    return api.get<{ pairs: IxenPair[]; signals: IxenSignal[] }>(
      `/ixen-system/dashboard?signals_limit=${signalsLimit}`
    )
  },

  async resetEaToken(ixenPairId: number, eaType: 'PROP' | 'BROKER'): Promise<{ ea_token: string; ea_type: string; ixen_pair_id: number }> {
    return api.post<{ ea_token: string; ea_type: string; ixen_pair_id: number }>(
      `/ixen-system/ea/session?ixen_pair_id=${ixenPairId}&ea_type=${eaType}`
    )
  },

  async getPairDetail(pairId: number): Promise<IxenPair & {
    prop_token: string | null; broker_token: string | null;
    prop_token_full: string | null; broker_token_full: string | null;
    cloud_url: string; display_name_raw: string;
  }> {
    return api.get(`/ixen-system/pairs/${pairId}`)
  },

  async updatePairFull(pairId: number, data: {
    display_name?: string; prop_login?: string; prop_server?: string;
    broker_login?: string; broker_server?: string;
    prop_provider?: string; prop_phase?: string; prop_size?: number;
    control_mode?: boolean; fnd_disciplined_mode?: boolean;
    fp2_risk_profile?: 'conservativo' | 'aggressivo';
  }): Promise<IxenPair & { account_changed: boolean }> {
    return api.put(`/ixen-system/pairs/${pairId}`, data)
  },

  async regenerateTokens(pairId: number): Promise<{
    prop_token: string | null; broker_token: string | null; warning: string;
  }> {
    return api.post(`/ixen-system/pairs/${pairId}/regenerate-tokens`)
  },

  async forceUnlockPair(pairId: number): Promise<{
    status: string; pair_id: number; closed_signals: number;
    closed_hedge_ops: number; message: string
  }> {
    return api.post(`/ixen-system/pairs/${pairId}/force-unlock`)
  },

  async stopPair(pairId: number): Promise<{ status: string; pair_id: number; message: string }> {
    return api.post(`/ixen-system/pairs/${pairId}/stop`)
  },

  async resetDailyLoss(pairId: number): Promise<{
    status: string; pair_id: number; reset_at: string; message: string
  }> {
    return api.post(`/ixen-system/pairs/${pairId}/reset-daily-loss`)
  },

  // ── State Machine ──────────────────────────────────────────────────────────

  async getHedgeOperations(params?: {
    status_filter?: string
    pair_id?: number
    include_closed?: boolean
    limit?: number
  }): Promise<{ operations: HedgeOperation[]; total: number; open_count: number }> {
    const qs = new URLSearchParams()
    if (params?.status_filter) qs.set('status_filter', params.status_filter)
    if (params?.pair_id != null) qs.set('pair_id', String(params.pair_id))
    if (params?.include_closed) qs.set('include_closed', 'true')
    if (params?.limit != null) qs.set('limit', String(params.limit))
    const q = qs.toString()
    return api.get(`/ixen-system/hedge/operations${q ? '?' + q : ''}`)
  },

  async getHedgeStatus(opId: number): Promise<HedgeOperation & {
    live_prop: LivePosition | null
    live_broker: LivePosition | null
    current_price: { bid: number; ask: number } | null
    recent_instructions: RecentInstruction[]
  }> {
    return api.get(`/ixen-system/hedge/status/${opId}`)
  },

  async activateBrokerShield(operationId: number): Promise<HedgeActionResponse> {
    return api.post('/ixen-system/hedge/broker-shield', { operation_id: operationId })
  },

  async runnerDetach(operationId: number, mode: 'NATURALE' | 'ATTIVO' = 'ATTIVO'): Promise<HedgeActionResponse> {
    return api.post('/ixen-system/hedge/runner-detach', { operation_id: operationId, mode })
  },

  async forceClose(operationId: number): Promise<HedgeActionResponse> {
    return api.post('/ixen-system/hedge/force-close', { operation_id: operationId })
  },

  async resolveManualOverride(operationId: number, resumeState = 'HEDGE'): Promise<HedgeActionResponse> {
    return api.post('/ixen-system/hedge/manual-override/resolve', {
      operation_id: operationId,
      resume_state: resumeState,
    })
  },

  async getHedgeConfig(): Promise<TrailingConfig> {
    return api.get('/ixen-system/hedge/config')
  },

  async updateHedgeConfig(updates: Partial<TrailingConfigUpdate>): Promise<TrailingConfig> {
    return api.put('/ixen-system/hedge/config', updates)
  },

  async getMarketHealth(): Promise<{
    is_ready: boolean
    warming_up: boolean
    is_connected: boolean
    live_bid: number
    live_ask: number
    spread_points: number
    price_age_seconds: number
    tick_count: number
    candles_m1: number
    candles_m5: number
    candles_m15: number
    last_error: string | null
  }> {
    return api.get('/ixen-system/market/health')
  },

  async getYomiSignal(pairId: number, fprMarginMode: string = 'massimale', fndLeverage: number = 15): Promise<YomiSignalResponse> {
    const qs = new URLSearchParams({ fpr_margin_mode: fprMarginMode, fnd_leverage: String(fndLeverage) })
    return api.get(`/ixen-system/yomi/${pairId}?${qs.toString()}`)
  },

  async getYomiLastSignal(asset: string = 'XAUUSD', ttlMinutes: number = 15): Promise<YomiLastSignalResponse> {
    const qs = new URLSearchParams({ asset, ttl_minutes: String(ttlMinutes) })
    return api.get(`/ixen-system/yomi/last-signal?${qs.toString()}`)
  },

  // Sniper Engine: admin-only, NOTIFY-only (no execution).
  async getSniperLastSignal(asset: string = 'XAUUSD', ttlMinutes: number = 10): Promise<SniperLastSignalResponse> {
    const qs = new URLSearchParams({ asset, ttl_minutes: String(ttlMinutes) })
    return api.get(`/ixen-system/sniper/last-signal?${qs.toString()}`)
  },

  // Sempai: alert esterni catturati via app/MacroDroid. Admin-only, NOTIFY-only.
  async getSempaiRecentAlerts(limit: number = 5): Promise<SempaiAlertItem[]> {
    return api.get(`/external/jifu-alerts/recent?limit=${limit}`)
  },

  async getMakatsuRecentSignals(limit: number = 5): Promise<MakatsuSignalItem[]> {
    return api.get(`/ixen-system/makatsu/recent-signals?limit=${limit}`)
  },

  async executeYomiSignal(pairId: number, body: {
    expected_direction: 'BUY' | 'SELL'
    generated_at: string
    fpr_margin_mode?: string
    fnd_leverage?: number
  }): Promise<SignalResponse> {
    return api.post<SignalResponse>(`/ixen-system/yomi/${pairId}/execute`, {
      expected_direction: body.expected_direction,
      generated_at: body.generated_at,
      fpr_margin_mode: body.fpr_margin_mode ?? 'massimale',
      fnd_leverage: body.fnd_leverage ?? 15,
    })
  },

  async getMarketContext(): Promise<MarketContextResponse> {
    return api.get('/ixen-system/market-context')
  },

  async getYomiSettings(pairId: number): Promise<YomiSettingsResponse> {
    return api.get(`/ixen-system/yomi/${pairId}/settings`)
  },

  async updateYomiSettings(pairId: number, data: YomiSettingsUpdate): Promise<{ ok: boolean; mode: string; auto_filter: string }> {
    return api.post(`/ixen-system/yomi/${pairId}/settings`, data)
  },

  // ── Admin Yomi Log ────────────────────────────────────────────────────────
  async getYomiLog(params: {
    from?: string
    to?: string
    decision?: string
    hour?: number
    signal_label?: string
    was_executed?: boolean
    direction?: string
    limit?: number
    offset?: number
  } = {}): Promise<YomiLogResponse> {
    const qs = new URLSearchParams()
    if (params.from)         qs.append('from', params.from)
    if (params.to)           qs.append('to', params.to)
    if (params.decision)     qs.append('decision', params.decision)
    if (params.hour != null) qs.append('hour', String(params.hour))
    if (params.signal_label) qs.append('signal_label', params.signal_label)
    if (params.was_executed != null) qs.append('was_executed', String(params.was_executed))
    if (params.direction)    qs.append('direction', params.direction)
    qs.append('limit', String(params.limit ?? 200))
    qs.append('offset', String(params.offset ?? 0))
    return api.get(`/admin/yomi-log?${qs.toString()}`)
  },

  async getYomiLogStats(days: number = 7): Promise<YomiLogStatsResponse> {
    return api.get(`/admin/yomi-log/stats?days=${days}`)
  },

  buildYomiLogCsvUrl(params: {
    from?: string
    to?: string
    decision?: string
    hour?: number
    signal_label?: string
    was_executed?: boolean
    direction?: string
  } = {}): string {
    const qs = new URLSearchParams()
    if (params.from)         qs.append('from', params.from)
    if (params.to)           qs.append('to', params.to)
    if (params.decision)     qs.append('decision', params.decision)
    if (params.hour != null) qs.append('hour', String(params.hour))
    if (params.signal_label) qs.append('signal_label', params.signal_label)
    if (params.was_executed != null) qs.append('was_executed', String(params.was_executed))
    if (params.direction)    qs.append('direction', params.direction)
    qs.append('format', 'csv')
    qs.append('limit', '5000')
    return `/api/v1/admin/yomi-log?${qs.toString()}`
  },

  buildYomiLogJsonUrl(params: {
    from?: string
    to?: string
    decision?: string
    hour?: number
    signal_label?: string
    was_executed?: boolean
    direction?: string
  } = {}): string {
    const qs = new URLSearchParams()
    if (params.from)         qs.append('from', params.from)
    if (params.to)           qs.append('to', params.to)
    if (params.decision)     qs.append('decision', params.decision)
    if (params.hour != null) qs.append('hour', String(params.hour))
    if (params.signal_label) qs.append('signal_label', params.signal_label)
    if (params.was_executed != null) qs.append('was_executed', String(params.was_executed))
    if (params.direction)    qs.append('direction', params.direction)
    qs.append('format', 'export_json')
    qs.append('limit', '5000')
    return `/api/v1/admin/yomi-log?${qs.toString()}`
  },
}

// ── Admin Yomi Log types ────────────────────────────────────────────────────
export interface YomiLogRow {
  id: number
  created_at: string | null
  m5_candle_ts: string | null
  decision: string | null
  reason: string | null
  direction: string | null
  regime: string | null
  bias: string | null
  adx_h1: number | null
  adx_m15: number | null
  adx_m5: number | null
  adx_phase: string | null
  htf_direction: string | null
  confidence: number | null
  signal_class: string | null
  signal_label: string | null
  hour_utc: number | null
  hour_quality: string | null
  recommendation_level: string | null
  recommendation_score: number | null
  reference_price: number | null
  was_executed: boolean | null
  signal_id: number | null
  prime_htf_override?: boolean | null
}

export interface YomiLogResponse {
  total: number
  limit: number
  offset: number
  count: number
  items: YomiLogRow[]
}

export interface YomiLogStatsResponse {
  window_days: number
  total: number
  executed: number
  by_decision: Record<string, number>
  by_label: Record<string, number>
  by_hour: Record<string, number>
}

// ── Tipi State Machine ─────────────────────────────────────────────────────

export type HedgeStatus =
  | 'HEDGE' | 'RECOVERING' | 'BROKER_SHIELD' | 'UNHEDGED'
  | 'RUNNER_NATURALE' | 'RUNNER_ATTIVO' | 'FORCE_CLOSE' | 'CLOSED' | 'MANUAL_OVERRIDE'

export interface HedgeOperation {
  id: number
  ixen_system_pair_id: number
  pair_name?: string
  signal_id: number | null
  signal_direction: string
  status: HedgeStatus
  previous_status: string | null
  prop_ticket: number | null
  broker_ticket: number | null
  prop_account: string | null
  broker_account: string | null
  prop_open_price: number | null
  broker_open_price: number | null
  prop_lots: number | null
  broker_lots: number | null
  sl_usd: number | null
  tp_usd: number | null
  expected_prop_sl: number | null
  expected_prop_tp: number | null
  expected_broker_sl: number | null
  expected_broker_tp: number | null
  prop_pnl: number | null
  broker_pnl: number | null
  total_pnl: number | null
  status_updated_at: string | null
  created_at: string | null
  closed_at: string | null
}

export interface LivePosition {
  ticket: number
  sl: number | null
  tp: number | null
  profit: number | null
}

export interface RecentInstruction {
  id: number
  action: string
  ticket: number
  sl: number | null
  tp: number | null
  status: string
  created_at: string
}

export interface HedgeActionResponse {
  status: string
  operation_id: number
  new_status: string
  message: string
}

export interface TrailingConfig {
  id: number
  user_id: number
  partial_lock_preset: string
  partial_lock_trigger_pct: number
  trail_step_pts: number
  trail_shift_pts: number
  buffer_pts: number
  unhedged_timeout_min: number
  force_close_timeout_min: number
  min_sl_delta_pts: number
  cooldown_s: number
  runner_mode: string
  updated_at: string | null
}

export interface TrailingConfigUpdate {
  partial_lock_preset?: 'CONSERVATIVE' | 'BALANCED' | 'AGGRESSIVE'
  partial_lock_trigger_pct?: number
  trail_step_pts?: number
  trail_shift_pts?: number
  buffer_pts?: number
  unhedged_timeout_min?: number
  force_close_timeout_min?: number
  min_sl_delta_pts?: number
  cooldown_s?: number
  runner_mode?: 'MANUAL' | 'AUTO_NOTIFY' | 'AUTONOMOUS'
}

// ── Tipi Yomi ──────────────────────────────────────────────────────────────

export interface YomiDiagnostics {
  regime: 'TREND_BULL' | 'TREND_BEAR' | 'RANGE' | 'UNDEFINED' | null
  bias: 'BUY' | 'SELL' | 'NONE' | null
  adx_h1: number | null
  adx_m15: number | null
  regime_reason: string | null
  use_m15_regime: boolean
  adx_threshold: number              // soglia principale (M15 quando use_m15_regime=true)
  adx_h1_filter_threshold: number    // filtro H1 (16) — sotto blocca sempre
  h1_candles_count: number
  h1_candles_min: number
  m15_candles_count: number
  m15_candles_min: number
}

export interface YomiSignalResponse {
  active: boolean
  reason?: string
  evaluated_at: string
  prop_direction?: 'BUY' | 'SELL'
  broker_direction?: 'BUY' | 'SELL'
  sl_percent?: number
  tp_percent?: number
  score?: number
  signal_class?: string
  message?: string
  generated_at?: string
  expires_at?: string
  invalid_price_threshold?: number
  h1_candles_count?: number
  h1_candles_min?: number
  diagnostics?: YomiDiagnostics | null
  user_facing?: YomiUserFacing | null
  // ── Arricchimento "trade reale" — presenti solo se segnale ATTIVO ──────
  entry_price?: number
  price_source?: string
  lots_prop?: number
  lots_broker?: number
  sl_prop_price?: number
  tp_prop_price?: number
  sl_broker_price?: number
  tp_broker_price?: number
  sl_distance_usd?: number
  tp_distance_usd?: number
  fpr_margin_mode?: string | null
  // ── Yomi v4.1 (30/04/2026) — Decision Engine layers + SessionQuality ──
  // Tutti opzionali: i campi vengono popolati solo dai segnali emessi
  // dall'engine v4.1; segnali v3 / engine spento → undefined.
  session_quality?: SessionQualityData | null
  scenario?: 'IMPULSE' | 'SQUEEZE' | 'TREND_PULLBACK' | 'RANGE' | 'EXHAUSTED' | 'UNKNOWN' | null
  price_invalidation?: number | null
  tags?: string[] | null
  impulse_exit?: ImpulseExitData | null
}

// Signal Persistence: ultimo segnale ALLOW Yomi recente.
// Endpoint ASSET-scoped (XAUUSD) — chiamato UNA volta sola per sessione,
// non per pair. Mostrato nel banner solo quando il segnale "live" è inattivo.
export interface YomiLastSignalResponse {
  active: boolean
  reason?: 'no_recent_allow' | 'expired' | 'unsupported_asset' | 'no_active_pairs'
  signal?: {
    decision_at: string       // ISO UTC
    expires_at: string        // ISO UTC
    age_seconds: number
    ttl_seconds: number
    direction: 'BUY' | 'SELL' | null     // lato BROKER
    prop_direction: 'BUY' | 'SELL' | null // lato PROP
    reference_price: number | null
    scenario: string | null
    confidence: number | null
    score: number | null
    signal_class: string | null
    signal_label: string | null
    regime: string | null
    bias: string | null
    adx_h1: number | null
    adx_m15: number | null
    adx_m5: number | null
    asset: string
  }
}

// ── Sniper Engine — last-signal (admin-only, NOTIFY only, no execution) ─────
// Asset-scoped, polling identico a yomi/last-signal. Reversal/mean-reversion,
// secondo motore indipendente da Yomi. Visibile solo agli admin in IxenSystem.
export interface SniperLastSignalResponse {
  active: boolean
  reason?: 'no_recent_allow' | 'expired' | 'unsupported_asset' | null
  signal?: {
    decision_at: string       // ISO UTC
    expires_at: string        // ISO UTC
    age_seconds: number
    ttl_seconds: number
    direction: 'BUY' | 'SELL' | null      // lato BROKER (reversal target)
    prop_direction: 'BUY' | 'SELL' | null // lato PROP
    scenario: 'REVERSAL_LONG' | 'REVERSAL_SHORT' | null
    signal_label: 'PREMIUM' | 'STANDARD' | 'CAUTION' | null
    score: number | null                  // 0.0 - 1.0
    reference_price: number | null
    adx_m5: number | null
    adx_h1: number | null
    h4_bias: string | null
    detectors: {
      liquidity_sweep:  { hit: boolean; side: string | null; level: number | null }
      rejection_candle: { hit: boolean; kind: string | null }
      volume_spike:     { hit: boolean; z_score: number | null }
      bb_extreme:       { hit: boolean; position_pct: number | null }
    }
    yomi_conflict: boolean
    yomi_direction: 'BUY' | 'SELL' | null
    yomi_scenario: string | null
    asset: string
  } | null
}

// Sempai: singolo alert esterno (XAUUSD M5) registrato dal bridge.
// NOTIFY-only: nessuna esecuzione automatica. score assente lato sorgente.
export interface MakatsuSignalItem {
  id: number
  direction: 'BUY' | 'SELL'   // direzione GREZZA del segnale Telegram (la UI mostra l'inversa)
  entry_price: number | null
  zone_low: number | null
  zone_high: number | null
  status: string
  received_at: string | null
}

export interface SempaiAlertItem {
  id: number
  direction: 'BUY' | 'SELL'
  score: number
  timeframe: string
  raw_text: string | null      // null per i non-admin (fonte redatta lato server)
  entry_price: number | null   // prezzo parsato lato server (visibile a tutti)
  status: string
  executed_by: string | null
  signal_id: number | null
  pair_id: number | null
  received_at: string | null   // ISO UTC (con suffisso Z)
}

// SessionQuality: badge informativo della fascia oraria.
// Nessun blocco lato motore — l'utente decide se eseguire.
export interface SessionQualityData {
  label: 'PRIME' | 'GOOD' | 'CAUTION' | 'AVOID'
  color: 'green' | 'blue' | 'yellow' | 'red'
  emoji?: string
  hour_utc?: number
  win_rate_pct: number
  avg_pts: number
  trade_count?: number
  session_name?: string
  message: string
}

// ImpulseExit: suggerimento exit anticipato per IMPULSE
// (consumato dal trailing_worker futuro — non agisce mai autonomo).
export interface ImpulseExitData {
  close_now: boolean
  reason: string
  roc_now?: number
  roc_prev?: number
  roc_acceleration?: number
  cci_now?: number
  cci_recovering?: boolean
  in_profit_pts?: number
  momentum_strength: number
}

export type YomiSignalLabel = 'PREMIUM' | 'STANDARD' | 'CAUTION' | 'NO SIGNAL'
export type YomiHourQuality = 'FAVORABLE_HOUR'
export type YomiRecommendationLevel = 'ENTER' | 'GOOD' | 'EVALUATE' | 'CAUTION' | 'DISCOURAGED' | 'NONE'

export interface YomiUserFacing {
  signal_label: YomiSignalLabel
  signal_color: string
  // hour: null per ore non favorevoli (silenzio: nessun badge in UI)
  hour: {
    hour_utc: number
    quality: YomiHourQuality
    icon: string
    label: string
    color: string
  } | null
  phase: {
    adx_phase: string | null
    label: string
    color: string
    icon: string
  }
  recommendation: {
    level: YomiRecommendationLevel
    label: string
    color: string
    icon: string
    score?: number
    details: string
  }
}

export interface MarketContextResponse {
  available: boolean
  reason?: string
  evaluated_at: string
  h1_candles_count: number
  h1_candles_min: number
  m15_candles_count: number
  m15_candles_min: number
  adx_threshold: number              // soglia principale (M15 o H1)
  adx_h1_filter_threshold: number    // filtro H1 (16)
  use_m15_regime: boolean
  regime: 'TREND_BULL' | 'TREND_BEAR' | 'RANGE' | 'UNDEFINED' | null
  bias: 'BUY' | 'SELL' | 'NONE' | null
  adx_h1: number | null
  ema_h1: number | null
  price_vs_ema: 'ABOVE' | 'BELOW' | null
  adx_m15: number | null
  ema_m15: number | null
  price_vs_ema_m15: 'ABOVE' | 'BELOW' | null
  regime_reason: string | null
}

export interface YomiSettingsResponse {
  mode: 'manual' | 'auto'
  auto_filter: 'safe' | 'standard'
  auto_accepted: boolean
  auto_accepted_at?: string
  yomi_min_class?: 'STRONG' | 'GOOD' | 'WEAK'
  sempai_auto?: boolean
  // Step 2: personalizzazione auto per coppia (frazioni: 0.012 = 1,2%)
  // auto_* = motore Yomi; sempai_auto_* = motore Sempai (separati)
  auto_sl_pct?: number | null
  auto_tp_pct?: number | null
  sempai_auto_sl_pct?: number | null
  sempai_auto_tp_pct?: number | null
  makatsu_auto_sl_pct?: number | null
  makatsu_auto_tp_pct?: number | null
  auto_window_start?: string | null   // "HH:MM" nel fuso dell'utente
  auto_window_end?: string | null
  auto_window_tz?: string | null
}

export interface YomiSettingsUpdate {
  mode: 'manual' | 'auto'
  auto_filter?: 'safe' | 'standard'
  accepted?: boolean
  yomi_min_class?: 'STRONG' | 'GOOD' | 'WEAK'
  sempai_auto?: boolean
  // Step 2 — semantica: campo assente = non toccare; 0 (numerici) / "" (orari) = azzera
  // auto_* = motore Yomi; sempai_auto_* = motore Sempai (separati)
  auto_sl_pct?: number
  auto_tp_pct?: number
  sempai_auto_sl_pct?: number
  sempai_auto_tp_pct?: number
  makatsu_auto_sl_pct?: number
  makatsu_auto_tp_pct?: number
  auto_window_start?: string
  auto_window_end?: string
  auto_window_tz?: string
}

export default ixenSystemService
